/** Порт SvgPathProperties (C#) для viewBox/bbox SVG path в рантайме HMI. */

/** Как в TranslateHelper: NormalizeGeometry + !StartsWith("M0,0") */
export function normalizeGeometryPath(value) {
    if (value == null) return value;
    if (!globalThis.window?._globalParams?.NormalizeGeometry) return value;
    if (/^M0,0/i.test(String(value).trim())) return value;
    return "M0,0" + value;
}

export function getGeometryPathViewBox(pathData, fallbackWidth, fallbackHeight) {
    if (!pathData) return null;

    try {
        const bbox = getSvgPathBBox(pathData, true);
        if (!bbox) return null;

        const width = bbox.width === 0 ? Number(fallbackWidth) || 0 : bbox.width;
        const height = bbox.height === 0 ? Number(fallbackHeight) || 0 : bbox.height;
        if (!width && !height) return null;

        return `${bbox.left} ${bbox.top} ${width || 1} ${height || 1}`;
    } catch (e) {
        console.warn("getGeometryPathViewBox:", e);
        return null;
    }
}

/** Аналог new SvgPath(data, unarc: true).GetBBox() из C#. */
export function getSvgPathBBox(pathData, unarc = true) {
    const boxes = buildCommandBBoxes(pathData, unarc);
    if (!boxes.length) return null;

    let bbox = boxes[0];
    for (let i = 1; i < boxes.length; i++) {
        bbox = mergeBBox(bbox, boxes[i]);
    }

    return {
        left: bbox.left,
        top: bbox.top,
        width: bbox.right - bbox.left,
        height: bbox.bottom - bbox.top,
    };
}

export function parseSvgPath(path) {
    path = !path ? "M0,0" : path;
    const segments = path.match(/([astvzqmhlc])([^astvzqmhlc]*)/gi);
    if (!segments?.length) {
        throw new Error(`No path elements found in string ${path}`);
    }

    const result = [];
    for (const match of segments) {
        let command = match[0];
        let type = command.toLowerCase();
        const args = parsePathValues(match.slice(1));

        if (type === "m" && args.length > 2) {
            result.push([command, splicePathArgs(args, 0, 2)]);
            type = "l";
            command = command === "m" ? "l" : "L";
        }

        if (PATH_COMMAND_LENGTH[type] === 0) {
            result.push([command, []]);
            continue;
        }

        while (args.length > 0) {
            if (args.length === PATH_COMMAND_LENGTH[type]) {
                result.push([command, splicePathArgs(args, 0, PATH_COMMAND_LENGTH[type])]);
                break;
            }
            if (args.length < PATH_COMMAND_LENGTH[type]) {
                throw new Error(
                    `Malformed path data: "${command}" must have ${PATH_COMMAND_LENGTH[type]} elements and has ${args.length}: ${match}`
                );
            }
            result.push([command, splicePathArgs(args, 0, PATH_COMMAND_LENGTH[type])]);
        }
    }

    return result;
}

const PATH_COMMAND_LENGTH = {
    a: 7, c: 6, h: 1, l: 2, m: 2, q: 4, s: 4, t: 2, v: 1, z: 0,
};
const PATH_TAU = Math.PI * 2;
const CBEZIER_MINMAX_EPSILON = 0.00000001;

function parsePathValues(args) {
    const numbers = args.match(/-?[0-9]*\.?[0-9]+(?:e[-+]?\d+)?/gi) || [];
    return numbers.map((n) => parseFloat(n));
}

function splicePathArgs(source, start, size) {
    const items = source.slice(start, start + size);
    source.splice(start, size);
    return items;
}

function minMaxQ(A) {
    let min = Math.min(A[0], A[2]);
    let max = Math.max(A[0], A[2]);
    if (A[1] > A[0] ? A[2] >= A[1] : A[2] <= A[1]) {
        return [min, max];
    }
    const E = (A[0] * A[2] - A[1] * A[1]) / (A[0] - 2 * A[1] + A[2]);
    return E < min ? [E, max] : [min, E];
}

function minMaxC(A) {
    const K = A[0] - 3 * A[1] + 3 * A[2] - A[3];
    if (Math.abs(K) < CBEZIER_MINMAX_EPSILON) {
        if (A[0] === A[3] && A[0] === A[1]) {
            return [A[0], A[3]];
        }
        return minMaxQ([
            A[0],
            -0.5 * A[0] + 1.5 * A[1],
            A[0] - 3.0 * A[1] + 3.0 * A[2],
        ]);
    }

    const T =
        -A[0] * A[2] +
        A[0] * A[3] -
        A[1] * A[2] -
        A[1] * A[3] +
        A[1] * A[1] +
        A[2] * A[2];

    if (T <= 0) {
        return [Math.min(A[0], A[3]), Math.max(A[0], A[3])];
    }

    const S = Math.sqrt(T);
    let min = Math.min(A[0], A[3]);
    let max = Math.max(A[0], A[3]);
    const L = A[0] - 2 * A[1] + A[2];

    for (let i = 1; i <= 2; i++) {
        const R = i === 1 ? (L + S) / K : (L - S) / K;
        if (R > 0 && R < 1) {
            const Q =
                A[0] * (1.0 - R) ** 3 +
                A[1] * 3.0 * (1.0 - R) ** 2 * R +
                A[2] * 3.0 * (1.0 - R) * R ** 2 +
                A[3] * R ** 3;
            if (Q < min) min = Q;
            if (Q > max) max = Q;
        }
    }

    return [min, max];
}

function lineBBox(fromX, fromY, toX, toY) {
    return {
        left: Math.min(fromX, toX),
        top: Math.min(fromY, toY),
        right: Math.max(fromX, toX),
        bottom: Math.max(fromY, toY),
    };
}

function bezierBBox(ax, ay, bx, by, cx, cy, dx, dy) {
    const isQuadratic = dx == null && dy == null;
    let minX;
    let maxX;
    let minY;
    let maxY;

    if (isQuadratic) {
        const qx = minMaxQ([ax, bx, cx]);
        const qy = minMaxQ([ay, by, cy]);
        minX = qx[0];
        maxX = qx[1];
        minY = qy[0];
        maxY = qy[1];
    } else {
        const cxMinMax = minMaxC([ax, bx, cx, dx]);
        const cyMinMax = minMaxC([ay, by, cy, dy]);
        minX = cxMinMax[0];
        maxX = cxMinMax[1];
        minY = cyMinMax[0];
        maxY = cyMinMax[1];
    }

    return { left: minX, top: minY, right: maxX, bottom: maxY };
}

function unitVectorAngle(ux, uy, vx, vy) {
    const sign = ux * vy - uy * vx < 0 ? -1 : 1;
    let dot = ux * vx + uy * vy;
    if (dot > 1.0) dot = 1.0;
    if (dot < -1.0) dot = -1.0;
    return sign * Math.acos(dot);
}

function getArcCenter(x1, y1, x2, y2, fa, fs, rx, ry, sinPhi, cosPhi) {
    const x1P = (cosPhi * (x1 - x2)) / 2 + (sinPhi * (y1 - y2)) / 2;
    const y1P = (-sinPhi * (x1 - x2)) / 2 + (cosPhi * (y1 - y2)) / 2;
    const rxSq = rx * rx;
    const rySq = ry * ry;
    const x1PSq = x1P * x1P;
    const y1PSq = y1P * y1P;

    let radicant = rxSq * rySq - rxSq * y1PSq - rySq * x1PSq;
    if (radicant < 0) radicant = 0;
    radicant /= rxSq * y1PSq + rySq * x1PSq;
    radicant = Math.sqrt(radicant) * (fa === fs ? -1 : 1);

    const cxp = (radicant * rx) / ry * y1P;
    const cyp = radicant * (-ry) / rx * x1P;
    const cx = cosPhi * cxp - sinPhi * cyp + (x1 + x2) / 2;
    const cy = sinPhi * cxp + cosPhi * cyp + (y1 + y2) / 2;

    const v1X = (x1P - cxp) / rx;
    const v1Y = (y1P - cyp) / ry;
    const v2X = (-x1P - cxp) / rx;
    const v2Y = (-y1P - cyp) / ry;

    let theta1 = unitVectorAngle(1, 0, v1X, v1Y);
    let deltaTheta = unitVectorAngle(v1X, v1Y, v2X, v2Y);

    if (fs === 0 && deltaTheta > 0) deltaTheta -= PATH_TAU;
    if (fs === 1 && deltaTheta < 0) deltaTheta += PATH_TAU;

    return { cx, cy, theta1, deltaTheta };
}

function approximateUnitArc(theta1, deltaTheta) {
    const alpha = (4.0 / 3.0) * Math.tan(deltaTheta / 4.0);
    const x1 = Math.cos(theta1);
    const y1 = Math.sin(theta1);
    const x2 = Math.cos(theta1 + deltaTheta);
    const y2 = Math.sin(theta1 + deltaTheta);
    return [
        x1, y1,
        x1 - y1 * alpha, y1 + x1 * alpha,
        x2 + y2 * alpha, y2 - x2 * alpha,
        x2, y2,
    ];
}

function arcToCurves(x1, y1, x2, y2, fa, fs, rx, ry, phi) {
    const result = [];
    const sinPhi = Math.sin((phi * PATH_TAU) / 360);
    const cosPhi = Math.cos((phi * PATH_TAU) / 360);

    const x1P = (cosPhi * (x1 - x2)) / 2 + (sinPhi * (y1 - y2)) / 2;
    const y1P = (-sinPhi * (x1 - x2)) / 2 + (cosPhi * (y1 - y2)) / 2;
    if (x1P === 0 && y1P === 0) return result;
    if (rx === 0 || ry === 0) return result;

    rx = Math.abs(rx);
    ry = Math.abs(ry);

    const lambda = (x1P * x1P) / (rx * rx) + (y1P * y1P) / (ry * ry);
    if (lambda > 1) {
        rx *= Math.sqrt(lambda);
        ry *= Math.sqrt(lambda);
    }

    const cc = getArcCenter(x1, y1, x2, y2, fa, fs, rx, ry, sinPhi, cosPhi);
    let theta1 = cc.theta1;
    let deltaTheta = cc.deltaTheta;

    const segments = Math.max(Math.ceil(Math.abs(deltaTheta) / (PATH_TAU / 4)), 1);
    deltaTheta /= segments;

    for (let i = 0; i < segments; i++) {
        const curve = approximateUnitArc(theta1, deltaTheta);
        for (let j = 0; j < curve.length; j += 2) {
            let x = curve[j];
            let y = curve[j + 1];
            x *= rx;
            y *= ry;
            const xp = cosPhi * x - sinPhi * y;
            const yp = sinPhi * x + cosPhi * y;
            curve[j] = xp + cc.cx;
            curve[j + 1] = yp + cc.cy;
        }
        result.push(curve);
        theta1 += deltaTheta;
    }

    return result;
}

function mergeBBox(target, source) {
    if (!source) return target;
    return {
        left: Math.min(target.left, source.left),
        top: Math.min(target.top, source.top),
        right: Math.max(target.right, source.right),
        bottom: Math.max(target.bottom, source.bottom),
    };
}

function buildCommandBBoxes(path, unarc = true) {
    const parsed = parseSvgPath(path);
    const boxes = [];
    const cur = [0, 0];
    let ringStart = [0, 0];
    let lastCurve = null;
    let prevPoint = [0, 0];

    const addMove = (x, y) => {
        cur[0] = x;
        cur[1] = y;
        ringStart = [x, y];
        boxes.push({ left: x, top: y, right: x, bottom: y });
    };

    const addLine = (x, y) => {
        boxes.push(lineBBox(cur[0], cur[1], x, y));
        cur[0] = x;
        cur[1] = y;
        lastCurve = null;
    };

    for (const [command, values] of parsed) {
        const type = command.toLowerCase();
        const abs = command === type.toUpperCase();

        if (type === "m") {
            addMove(abs ? values[0] : cur[0] + values[0], abs ? values[1] : cur[1] + values[1]);
        } else if (type === "l") {
            addLine(abs ? values[0] : cur[0] + values[0], abs ? values[1] : cur[1] + values[1]);
        } else if (type === "h") {
            addLine(abs ? values[0] : cur[0] + values[0], cur[1]);
        } else if (type === "v") {
            addLine(cur[0], abs ? values[0] : cur[1] + values[0]);
        } else if (type === "z") {
            addLine(ringStart[0], ringStart[1]);
        } else if (type === "c") {
            const x1 = abs ? values[0] : cur[0] + values[0];
            const y1 = abs ? values[1] : cur[1] + values[1];
            const x2 = abs ? values[2] : cur[0] + values[2];
            const y2 = abs ? values[3] : cur[1] + values[3];
            const x = abs ? values[4] : cur[0] + values[4];
            const y = abs ? values[5] : cur[1] + values[5];
            boxes.push(bezierBBox(cur[0], cur[1], x1, y1, x2, y2, x, y));
            cur[0] = x;
            cur[1] = y;
            lastCurve = { cp2X: x2, cp2Y: y2, quadratic: false };
        } else if (type === "s") {
            let x1 = cur[0];
            let y1 = cur[1];
            if (lastCurve && !lastCurve.quadratic) {
                x1 = 2 * cur[0] - lastCurve.cp2X;
                y1 = 2 * cur[1] - lastCurve.cp2Y;
            }
            const x2 = abs ? values[0] : cur[0] + values[0];
            const y2 = abs ? values[1] : cur[1] + values[1];
            const x = abs ? values[2] : cur[0] + values[2];
            const y = abs ? values[3] : cur[1] + values[3];
            boxes.push(bezierBBox(cur[0], cur[1], x1, y1, x2, y2, x, y));
            cur[0] = x;
            cur[1] = y;
            lastCurve = { cp2X: x2, cp2Y: y2, quadratic: false };
        } else if (type === "q") {
            const x1 = abs ? values[0] : cur[0] + values[0];
            const y1 = abs ? values[1] : cur[1] + values[1];
            const x = abs ? values[2] : cur[0] + values[2];
            const y = abs ? values[3] : cur[1] + values[3];
            boxes.push(bezierBBox(cur[0], cur[1], x1, y1, x, y));
            cur[0] = x;
            cur[1] = y;
            prevPoint = [x1, y1];
            lastCurve = { cp2X: x1, cp2Y: y1, quadratic: true };
        } else if (type === "t") {
            let x1 = cur[0];
            let y1 = cur[1];
            if (lastCurve?.quadratic) {
                x1 = 2 * cur[0] - prevPoint[0];
                y1 = 2 * cur[1] - prevPoint[1];
            }
            const x = abs ? values[0] : cur[0] + values[0];
            const y = abs ? values[1] : cur[1] + values[1];
            boxes.push(bezierBBox(cur[0], cur[1], x1, y1, x, y));
            prevPoint = [x1, y1];
            cur[0] = x;
            cur[1] = y;
            lastCurve = { cp2X: x1, cp2Y: y1, quadratic: true };
        } else if (type === "a") {
            const rx = values[0];
            const ry = values[1];
            const angle = values[2];
            const largeArc = values[3] === 1;
            const sweep = values[4] === 1;
            const x = abs ? values[5] : cur[0] + values[5];
            const y = abs ? values[6] : cur[1] + values[6];

            if (unarc) {
                const curves = arcToCurves(cur[0], cur[1], x, y, largeArc ? 1 : 0, sweep ? 1 : 0, rx, ry, angle);
                if (!curves.length) {
                    addLine(x, y);
                } else {
                    for (const segment of curves) {
                        boxes.push(
                            bezierBBox(
                                cur[0], cur[1],
                                segment[2], segment[3],
                                segment[4], segment[5],
                                segment[6], segment[7]
                            )
                        );
                        cur[0] = segment[6];
                        cur[1] = segment[7];
                    }
                }
            } else {
                addLine(x, y);
            }
            lastCurve = null;
        }
    }

    return boxes;
}
